import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Drawer, Modal, message } from "antd";
import { CareShell } from "../../components/care/CareLayout";
import CopyableText from "../../common/CopyableText";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";

const INQUIRY_STATUSES = [
  ["READ", "Mark read", "ti-mail-opened"],
  ["PROCESSED", "Processed", "ti-circle-check"],
  ["NEEDS_FOLLOW_UP", "Needs follow up", "ti-phone-call"],
  ["CLOSED", "Closed", "ti-lock"],
];

const emptyForm = {
  vehicleId: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  zipCode: "",
  scheduleVisit: false,
  visitDate: "",
  visitTime: "",
  messageToSeller: "",
  status: "NEW",
};

const defaultFilters = {
  vehicleId: "",
  status: "",
  query: "",
};

const filtersFromSearchParams = (searchParams) => ({
  vehicleId: searchParams.get("vehicleId") || "",
  status: searchParams.get("status") || "",
  query: searchParams.get("query") || "",
});

const filtersToSearchParams = (filters) => {
  const searchParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    const normalized = String(value || "").trim();
    if (normalized) searchParams.set(key, normalized);
  });
  return searchParams;
};

const pageItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const fullName = (inquiry = {}) =>
  firstText(
    [inquiry.firstName, inquiry.lastName].filter(Boolean).join(" "),
    inquiry.customerName,
    inquiry.name,
    "Customer",
  );

const inquiryId = (inquiry = {}, index = 0) =>
  firstText(inquiry.id, inquiry.customerInquiryId, inquiry.inquiryId, index);

const noteId = (note = {}, index = 0) => firstText(note.id, note.noteId, note.inquiryNoteId, index);

const formatStatus = (value) =>
  firstText(value, "NEW")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const statusClass = (value) =>
  `is-${firstText(value, "NEW")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")}`;

const formatDateTime = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formVisitParts = (value) => {
  if (!value) return { scheduleVisit: false, visitDate: "", visitTime: "" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { scheduleVisit: false, visitDate: "", visitTime: "" };
  const visitDate = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
  const visitTime = [date.getHours(), date.getMinutes()].map((part) => String(part).padStart(2, "0")).join(":");
  return { scheduleVisit: true, visitDate, visitTime };
};

const scheduledVisitAtFromForm = (form = {}) => {
  if (!form.scheduleVisit || !form.visitDate || !form.visitTime) return null;
  const date = new Date(`${form.visitDate}T${form.visitTime}:00`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const normalizeNote = (note = {}, index = 0) => ({
  id: noteId(note, index),
  text: firstText(note.text, note.note, note.notes, note.message, note.body),
  authorName: firstText(note.authorName, note.createdByName, note.createdBy, "Staff"),
  createdAt: firstText(note.createdAt, note.createdDate, note.updatedAt),
});

const normalizeInquiry = (inquiry = {}, index = 0) => ({
  ...inquiry,
  id: inquiryId(inquiry, index),
  vehicleId: firstText(inquiry.vehicleId),
  firstName: firstText(inquiry.firstName),
  lastName: firstText(inquiry.lastName),
  name: fullName(inquiry),
  email: firstText(inquiry.email, inquiry.emailAddress),
  phone: firstText(inquiry.phone, inquiry.phoneNumber),
  zipCode: firstText(inquiry.zipCode, inquiry.postalCode),
  messageToSeller: firstText(inquiry.messageToSeller, inquiry.message),
  scheduledVisitAt: firstText(inquiry.scheduledVisitAt, inquiry.visitAt, inquiry.appointmentAt),
  createdAt: firstText(inquiry.createdAt, inquiry.createdDate, inquiry.submittedAt, inquiry.updatedAt),
  status: firstText(inquiry.status, "NEW").toUpperCase(),
  read: Boolean(inquiry.read || inquiry.readAt || inquiry.status === "READ"),
  archived: Boolean(inquiry.archived || inquiry.archivedAt || inquiry.status === "ARCHIVED"),
  notes: pageItems(inquiry.notes || inquiry.inquiryNotes || inquiry.customerInquiryNotes).map(normalizeNote),
});

const formFromInquiry = (inquiry = {}) => {
  const visitParts = formVisitParts(inquiry.scheduledVisitAt);
  return {
    vehicleId: inquiry.vehicleId || "",
    firstName: inquiry.firstName || "",
    lastName: inquiry.lastName || "",
    email: inquiry.email || "",
    phone: inquiry.phone || "",
    zipCode: inquiry.zipCode || "",
    ...visitParts,
    messageToSeller: inquiry.messageToSeller || "",
    status: inquiry.status || "NEW",
  };
};

const payloadFromForm = (form) => ({
  vehicleId: form.vehicleId.trim(),
  firstName: form.firstName.trim(),
  lastName: form.lastName.trim(),
  email: form.email.trim(),
  phone: form.phone.trim(),
  zipCode: form.zipCode.trim(),
  scheduledVisitAt: scheduledVisitAtFromForm(form),
  messageToSeller: form.messageToSeller.trim(),
  status: form.status,
});

export default function CustomerInquiriesPage() {
  const { user } = useAuth();
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const actorId = user?.id || user?.userId || "";
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingInquiry, setEditingInquiry] = useState(null);
  const [selectedInquiryId, setSelectedInquiryId] = useState(() => searchParams.get("inquiryId") || "");
  const [form, setForm] = useState(emptyForm);
  const [noteTextById, setNoteTextById] = useState({});
  const [filters, setFilters] = useState(() => filtersFromSearchParams(searchParams));
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState("");

  const workspaceParams = useMemo(
    () => selectedWorkspaceId
      ? { wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId, actorId, sort: "createdAt,desc" }
      : { actorId, sort: "createdAt,desc" },
    [actorId, selectedWorkspaceId],
  );

  const loadInquiries = async () => {
    if (!workspaceListReady) return;
    setLoading(true);
    setError("");
    try {
      const data = await careService.customerInquiries.list(workspaceParams);
      setInquiries(pageItems(data).map(normalizeInquiry));
    } catch (nextError) {
      setInquiries([]);
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to load customer inquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;

    const run = async () => {
      if (!workspaceListReady) return;
      setLoading(true);
      setError("");
      try {
        const data = await careService.customerInquiries.list(workspaceParams);
        if (!active) return;
        setInquiries(pageItems(data).map(normalizeInquiry));
      } catch (nextError) {
        if (!active) return;
        setInquiries([]);
        setError(nextError?.response?.data?.message || nextError?.message || "Unable to load customer inquiries.");
      } finally {
        if (active) setLoading(false);
      }
    };

    run();

    return () => {
      active = false;
    };
  }, [workspaceListReady, workspaceParams]);

  useEffect(() => {
    const nextFilters = filtersFromSearchParams(searchParams);
    setFilters((current) => (
      JSON.stringify(current) === JSON.stringify(nextFilters) ? current : nextFilters
    ));
  }, [searchParams]);

  useEffect(() => {
    const inquiryParam = searchParams.get("inquiryId") || "";
    if (inquiryParam) {
      setSelectedInquiryId(inquiryParam);
    }
  }, [searchParams]);

  const updateFilters = (nextFilters) => {
    setFilters(nextFilters);
    setSearchParams(filtersToSearchParams(nextFilters), { replace: true });
  };
  const closeSelectedInquiry = () => {
    setSelectedInquiryId("");
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("inquiryId");
    setSearchParams(nextSearchParams, { replace: true });
  };

  const filteredInquiries = useMemo(() => {
    const vehicleFilter = filters.vehicleId.trim().toLowerCase();
    const statusFilter = filters.status.trim().toUpperCase();
    const query = filters.query.trim().toLowerCase();
    return inquiries.filter((inquiry) => {
      if (vehicleFilter && !String(inquiry.vehicleId || "").toLowerCase().includes(vehicleFilter)) return false;
      if (statusFilter && inquiry.status !== statusFilter) return false;
      if (query) {
        const haystack = [
          inquiry.name,
          inquiry.email,
          inquiry.phone,
          inquiry.zipCode,
          inquiry.vehicleId,
          inquiry.messageToSeller,
          inquiry.status,
        ].join(" ").toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [filters, inquiries]);

  const sortedInquiries = useMemo(
    () => [...filteredInquiries].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
    [filteredInquiries],
  );
  const selectedInquiry = useMemo(
    () => inquiries.find((inquiry) => inquiry.id === selectedInquiryId) || null,
    [inquiries, selectedInquiryId],
  );

  const upsertInquiry = (record) => {
    const normalized = normalizeInquiry(record);
    setInquiries((current) => {
      const exists = current.some((item) => item.id === normalized.id);
      return exists
        ? current.map((item) => (item.id === normalized.id ? { ...item, ...normalized } : item))
        : [normalized, ...current];
    });
  };

  const updateInquiryLocal = (id, updates) => {
    setInquiries((current) => current.map((item) => (item.id === id ? { ...item, ...updates } : item)));
  };

  const openCreate = () => {
    setEditingInquiry(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (inquiry) => {
    setEditingInquiry(inquiry);
    setForm(formFromInquiry(inquiry));
    setFormOpen(true);
  };

  const saveInquiry = async () => {
    setSaving(true);
    try {
      const payload = payloadFromForm(form);
      const saved = editingInquiry
        ? await careService.customerInquiries.update(editingInquiry.id, payload)
        : await careService.customerInquiries.create(payload);
      upsertInquiry(saved || { ...editingInquiry, ...payload });
      setFormOpen(false);
      message.success(editingInquiry ? "Inquiry updated." : "Inquiry created.");
    } catch (nextError) {
      message.error(nextError?.response?.data?.message || nextError?.message || "Unable to save inquiry.");
    } finally {
      setSaving(false);
    }
  };

  const runInquiryAction = async (inquiry, actionKey, run, successText, localUpdates = {}) => {
    setActionLoading(`${inquiry.id}:${actionKey}`);
    try {
      const result = await run();
      if (result && typeof result === "object" && inquiryId(result)) {
        upsertInquiry(result);
      } else {
        updateInquiryLocal(inquiry.id, localUpdates);
      }
      message.success(successText);
    } catch (nextError) {
      message.error(nextError?.response?.data?.message || nextError?.message || "Unable to update inquiry.");
    } finally {
      setActionLoading("");
    }
  };

  const changeStatus = (inquiry, status) => {
    const localUpdates = {
      status,
      read: status === "READ" ? true : inquiry.read,
      archived: status === "ARCHIVED" ? true : inquiry.archived,
    };
    Modal.confirm({
      title: `Mark inquiry as ${formatStatus(status)}?`,
      content: `This will update the status for ${inquiry.name}.`,
      okText: `Mark ${formatStatus(status)}`,
      cancelText: "Cancel",
      centered: true,
      onOk: () => runInquiryAction(
        inquiry,
        status,
        () => status === "READ"
          ? careService.customerInquiries.markRead(inquiry.id)
          : careService.customerInquiries.updateStatus(inquiry.id, status),
        `Inquiry marked ${formatStatus(status)}.`,
        localUpdates,
      ),
    });
  };

  const archiveInquiry = (inquiry) => {
    Modal.confirm({
      title: "Archive inquiry?",
      content: `This will archive the inquiry from ${inquiry.name}.`,
      okText: "Archive",
      cancelText: "Cancel",
      centered: true,
      onOk: () => runInquiryAction(
        inquiry,
        "archive",
        () => careService.customerInquiries.archive(inquiry.id),
        "Inquiry archived.",
        { status: "ARCHIVED", archived: true },
      ),
    });
  };

  const deleteInquiry = (inquiry) => {
    Modal.confirm({
      title: "Delete inquiry?",
      content: `This will remove the inquiry from ${inquiry.name}.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        setActionLoading(`${inquiry.id}:delete`);
        try {
          await careService.customerInquiries.delete(inquiry.id);
          setInquiries((current) => current.filter((item) => item.id !== inquiry.id));
          if (selectedInquiryId === inquiry.id) setSelectedInquiryId("");
          message.success("Inquiry deleted.");
        } catch (nextError) {
          message.error(nextError?.response?.data?.message || nextError?.message || "Unable to delete inquiry.");
        } finally {
          setActionLoading("");
        }
      },
    });
  };

  const addNote = async (inquiry) => {
    const text = firstText(noteTextById[inquiry.id]);
    if (!text) {
      message.warning("Add a note first.");
      return;
    }
    setActionLoading(`${inquiry.id}:note`);
    try {
      const result = await careService.customerInquiries.addNote(inquiry.id, { note: text, text });
      const notePayload = result && typeof result === "object" && firstText(result.note, result.text, result.id)
        ? result
        : { note: text, createdAt: new Date().toISOString(), createdBy: user?.name || user?.email || "Staff" };
      const nextNote = normalizeNote(notePayload);
      updateInquiryLocal(inquiry.id, { notes: [nextNote, ...(inquiry.notes || [])] });
      setNoteTextById((current) => ({ ...current, [inquiry.id]: "" }));
      message.success("Note added.");
    } catch (nextError) {
      message.error(nextError?.response?.data?.message || nextError?.message || "Unable to add note.");
    } finally {
      setActionLoading("");
    }
  };

  return (
    <CareShell className="dashboard-content vehicles-page customer-inquiries-page">
      <div className="vehicles-page-head">
        <div className="page-title">
          <span className="section-eyebrow">Customers</span>
          <h1>Customer Inquiries</h1>
          <p>Review, update, and follow up on guest interest forms submitted from public inventory pages.</p>
        </div>
        <div className="vehicles-actions">
          <button className="mini-btn" type="button" onClick={loadInquiries}><i className="ti ti-refresh" /> Refresh</button>
          <button className="mini-btn" type="button" onClick={openCreate}><i className="ti ti-plus" /> Add Inquiry</button>
          <Link className="mini-btn" to="/vehicles"><i className="ti ti-car" /> Inventory</Link>
        </div>
      </div>

      {error ? <p className="form-error">{error}</p> : null}

      <div className="customer-inquiries-filter">
        <label>
          <span>Vehicle inquiry</span>
          <input
            value={filters.vehicleId}
            onChange={(event) => updateFilters({ ...filters, vehicleId: event.target.value })}
            placeholder="Vehicle ID"
          />
        </label>
        <label>
          <span>Status</span>
          <select
            value={filters.status}
            onChange={(event) => updateFilters({ ...filters, status: event.target.value })}
          >
            <option value="">All statuses</option>
            <option value="NEW">New</option>
            <option value="READ">Read</option>
            <option value="PROCESSED">Processed</option>
            <option value="NEEDS_FOLLOW_UP">Needs follow up</option>
            <option value="CLOSED">Closed</option>
            <option value="ARCHIVED">Archived</option>
          </select>
        </label>
        <label>
          <span>Search</span>
          <input
            value={filters.query}
            onChange={(event) => updateFilters({ ...filters, query: event.target.value })}
            placeholder="Name, email, phone, message"
          />
        </label>
        <button className="mini-btn" type="button" onClick={() => updateFilters(defaultFilters)}>
          <i className="ti ti-refresh" /> Reset
        </button>
      </div>

      <p className="customer-inquiries-count">
        {loading ? "Loading inquiries..." : `${sortedInquiries.length} of ${inquiries.length} inquiries shown`}
      </p>

      <div className="customer-inquiries-list">
        {loading ? (
          <p className="vehicle-inline-empty">Loading customer inquiries...</p>
        ) : sortedInquiries.length ? sortedInquiries.map((inquiry) => (
          <article className={`customer-inquiry-card${inquiry.archived ? " is-archived" : ""}`} key={inquiry.id}>
            <div className="customer-inquiry-card__head">
              <span className="tiny-icon"><i className="ti ti-message-circle" /></span>
              <div>
                <strong>{inquiry.name}</strong>
                <small>{formatDateTime(inquiry.createdAt)} · {inquiry.email || inquiry.phone || "No contact provided"}</small>
              </div>
              <em className={`customer-inquiry-status ${statusClass(inquiry.status)}`}>
                <span>Status</span>{formatStatus(inquiry.status)}
              </em>
            </div>
            <div className="customer-inquiry-card__summary">
              <p>
                {inquiry.vehicleId ? (
                  <>
                    <span className="customer-inquiry-summary-label">Vehicle inquiry</span>
                    <CopyableText
                      className="customer-inquiry-copyable-id"
                      displayValue={inquiry.vehicleId}
                      value={inquiry.vehicleId}
                    />
                  </>
                ) : "General customer inquiry"}
              </p>
              <span><i className="ti ti-note" /> {inquiry.notes?.length || 0} notes</span>
              {inquiry.scheduledVisitAt ? (
                <span><i className="ti ti-calendar-clock" /> {formatDateTime(inquiry.scheduledVisitAt)}</span>
              ) : null}
            </div>
            <div className="customer-inquiry-card__footer">
              <button className="mini-btn inquiry-action-view" type="button" onClick={() => setSelectedInquiryId(inquiry.id)}>
                <i className="ti ti-eye" /> View message
              </button>
              <a className="mini-btn inquiry-action-email" href={inquiry.email ? `mailto:${inquiry.email}` : undefined} aria-disabled={!inquiry.email}>
                <i className="ti ti-mail" /> Email
              </a>
              <a className="mini-btn inquiry-action-call" href={inquiry.phone ? `tel:${inquiry.phone}` : undefined} aria-disabled={!inquiry.phone}>
                <i className="ti ti-phone" /> Call
              </a>
            </div>
          </article>
        )) : (
          <p className="vehicle-inline-empty">No customer inquiries have been submitted yet.</p>
        )}
      </div>

      <Drawer
        className="customer-inquiry-message-drawer"
        width={420}
        title={selectedInquiry ? selectedInquiry.name : "Customer inquiry"}
        open={Boolean(selectedInquiry)}
        onClose={closeSelectedInquiry}
        destroyOnClose
      >
        {selectedInquiry ? (
          <div className="customer-inquiry-drawer">
            <div className="customer-inquiry-drawer__status">
              <span className="tiny-icon"><i className="ti ti-message-circle" /></span>
              <div>
                <strong>{formatStatus(selectedInquiry.status)}</strong>
                <small>{formatDateTime(selectedInquiry.createdAt)}</small>
              </div>
            </div>
            <div className="customer-inquiry-actions">
              {INQUIRY_STATUSES.map(([status, label, icon]) => (
                <button
                  className={`mini-btn${selectedInquiry.status === status ? " active" : ""}`}
                  disabled={Boolean(actionLoading)}
                  key={status}
                  type="button"
                  onClick={() => changeStatus(selectedInquiry, status)}
                >
                  <i className={`ti ${icon}`} /> {label}
                </button>
              ))}
              <button className="mini-btn warning" disabled={Boolean(actionLoading)} type="button" onClick={() => archiveInquiry(selectedInquiry)}>
                <i className="ti ti-archive" /> Archive
              </button>
              <button className="mini-btn" disabled={Boolean(actionLoading)} type="button" onClick={() => openEdit(selectedInquiry)}>
                <i className="ti ti-pencil" /> Update
              </button>
              <button className="mini-btn danger" disabled={Boolean(actionLoading)} type="button" onClick={() => deleteInquiry(selectedInquiry)}>
                <i className="ti ti-trash" /> Delete
              </button>
            </div>
            <dl className="customer-inquiry-drawer__details">
              <div><dt>Email</dt><dd>{selectedInquiry.email || "Not provided"}</dd></div>
              <div><dt>Phone</dt><dd>{selectedInquiry.phone || "Not provided"}</dd></div>
              <div><dt>ZIP</dt><dd>{selectedInquiry.zipCode || "Not provided"}</dd></div>
              <div>
                <dt>Vehicle</dt>
                <dd>{selectedInquiry.vehicleId ? <Link to={`/vehicles/${selectedInquiry.vehicleId}`}>View vehicle</Link> : "Not provided"}</dd>
              </div>
              <div>
                <dt>Scheduled visit</dt>
                <dd>{selectedInquiry.scheduledVisitAt ? formatDateTime(selectedInquiry.scheduledVisitAt) : "Not requested"}</dd>
              </div>
            </dl>
            <section className="customer-inquiry-drawer__section">
              <h2>Message</h2>
              <p className="customer-inquiry-message">{selectedInquiry.messageToSeller || "No message was included with this inquiry."}</p>
            </section>
            <section className="customer-inquiry-notes">
              <div className="customer-inquiry-notes__head">
                <strong>Notes</strong>
                <span>{selectedInquiry.notes?.length || 0}</span>
              </div>
              {selectedInquiry.notes?.length ? selectedInquiry.notes.map((note) => (
                <div className="customer-inquiry-note" key={note.id}>
                  <p>{note.text}</p>
                  <small>{note.authorName} · {formatDateTime(note.createdAt)}</small>
                </div>
              )) : <p className="customer-inquiry-note-empty">No notes added yet.</p>}
              <div className="customer-inquiry-note-form">
                <textarea
                  rows={3}
                  value={noteTextById[selectedInquiry.id] || ""}
                  onChange={(event) => setNoteTextById((current) => ({ ...current, [selectedInquiry.id]: event.target.value }))}
                  placeholder="Add a follow-up note"
                />
                <button
                  className="mini-btn"
                  disabled={actionLoading === `${selectedInquiry.id}:note`}
                  type="button"
                  onClick={() => addNote(selectedInquiry)}
                >
                  <i className="ti ti-note" /> Add note
                </button>
              </div>
            </section>
          </div>
        ) : null}
      </Drawer>

      <Modal
        open={formOpen}
        title={editingInquiry ? "Update customer inquiry" : "Add customer inquiry"}
        onCancel={() => setFormOpen(false)}
        onOk={saveInquiry}
        okText={editingInquiry ? "Update" : "Create"}
        confirmLoading={saving}
        destroyOnClose
      >
        <div className="customer-inquiry-form">
          <label>
            <span>Vehicle ID</span>
            <input value={form.vehicleId} onChange={(event) => setForm((current) => ({ ...current, vehicleId: event.target.value }))} />
          </label>
          <label>
            <span>First name</span>
            <input value={form.firstName} onChange={(event) => setForm((current) => ({ ...current, firstName: event.target.value }))} />
          </label>
          <label>
            <span>Last name</span>
            <input value={form.lastName} onChange={(event) => setForm((current) => ({ ...current, lastName: event.target.value }))} />
          </label>
          <label>
            <span>Email</span>
            <input type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} />
          </label>
          <label>
            <span>Phone</span>
            <input value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} />
          </label>
          <label>
            <span>ZIP</span>
            <input value={form.zipCode} onChange={(event) => setForm((current) => ({ ...current, zipCode: event.target.value }))} />
          </label>
          <label>
            <span>Status</span>
            <select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}>
              <option value="NEW">New</option>
              <option value="READ">Read</option>
              <option value="PROCESSED">Processed</option>
              <option value="NEEDS_FOLLOW_UP">Needs follow up</option>
              <option value="CLOSED">Closed</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </label>
          <label>
            <span>Schedule visit?</span>
            <select
              value={form.scheduleVisit ? "yes" : "no"}
              onChange={(event) => setForm((current) => ({
                ...current,
                scheduleVisit: event.target.value === "yes",
                visitDate: event.target.value === "yes" ? current.visitDate : "",
                visitTime: event.target.value === "yes" ? current.visitTime : "",
              }))}
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </label>
          {form.scheduleVisit ? (
            <>
              <label>
                <span>Visit date</span>
                <input type="date" value={form.visitDate} onChange={(event) => setForm((current) => ({ ...current, visitDate: event.target.value }))} />
              </label>
              <label>
                <span>Visit time</span>
                <input type="time" value={form.visitTime} onChange={(event) => setForm((current) => ({ ...current, visitTime: event.target.value }))} />
              </label>
            </>
          ) : null}
          <label className="full-span">
            <span>Message to seller</span>
            <textarea rows={4} value={form.messageToSeller} onChange={(event) => setForm((current) => ({ ...current, messageToSeller: event.target.value }))} />
          </label>
        </div>
      </Modal>
    </CareShell>
  );
}
